import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang, BOOKING_URL } from "../i18n";
import { EASE } from "./Reveal";

const MENU_MOTION = {
  initial: { opacity: 0, y: -12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
  transition: { duration: 0.35, ease: EASE },
};

const Navbar = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const langCls = (active) => {
    if (scrolled) return active ? "text-[#14213D]" : "text-[#14213D]/40";
    return active ? "text-[#F9F8F6]" : "text-[#F9F8F6]/45";
  };

  const links = [
    { href: "#camere", label: t.nav.rooms, id: "rooms" },
    { href: "#servizi", label: t.nav.amenities, id: "amenities" },
    { href: "#galleria", label: t.nav.gallery, id: "gallery" },
    { href: "#dintorni", label: t.nav.surroundings, id: "surroundings" },
    { href: "#recensioni", label: t.nav.reviews, id: "reviews" },
    { href: "#contatti", label: t.nav.contact, id: "contact" },
  ];

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-500 ${
        scrolled ? "bg-[#F9F8F6]/80 backdrop-blur-xl shadow-[0_1px_0_rgba(26,26,26,0.08)]" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex items-center">
          <img
            src={scrolled ? "/assets/logo.png" : "/assets/logo-light.png"}
            alt="King's Place Guest House"
            className="h-9 md:h-10 w-auto transition-opacity duration-300"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.id}
              href={l.href}
              data-testid={`nav-link-${l.id}`}
              className={`link-underline text-sm tracking-wide transition-colors duration-300 ${
                scrolled ? "text-[#14213D]/80 hover:text-[#14213D]" : "text-[#F9F8F6]/85 hover:text-[#F9F8F6]"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button
            data-testid="lang-toggle"
            aria-label="Switch language"
            onClick={() => setLang(lang === "it" ? "en" : "it")}
            className={`text-xs tracking-[0.2em] font-medium uppercase transition-colors duration-300 ${
              scrolled ? "text-[#14213D]/70 hover:text-[#14213D]" : "text-[#F9F8F6]/80 hover:text-[#F9F8F6]"
            }`}
          >
            <span className={langCls(lang === "it")}>IT</span>
            <span className="mx-1.5 text-[#D39A2E]">/</span>
            <span className={langCls(lang === "en")}>EN</span>
          </button>
          <a
            data-testid="booking-cta-nav"
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={`hidden sm:inline-flex items-center rounded-full px-6 py-2.5 text-sm tracking-wide transition-colors duration-300 ${
              scrolled
                ? "bg-[#14213D] text-[#F9F8F6] hover:bg-[#2852A6]"
                : "bg-[#E8B54A] text-[#14213D] hover:bg-[#F9F8F6]"
            }`}
          >
            {t.nav.book}
          </a>
          <button
            data-testid="mobile-menu-button"
            aria-label="Menu"
            onClick={() => setOpen(!open)}
            className={`lg:hidden p-2 transition-colors duration-300 ${scrolled || open ? "text-[#14213D]" : "text-[#F9F8F6]"}`}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            {...MENU_MOTION}
            className="lg:hidden bg-[#F9F8F6]/95 backdrop-blur-xl border-t border-[#14213D]/10 px-6 py-6 flex flex-col gap-5"
          >
            {links.map((l) => (
              <a
                key={l.id}
                href={l.href}
                data-testid={`mobile-nav-link-${l.id}`}
                onClick={() => setOpen(false)}
                className="font-serif text-2xl"
              >
                {l.label}
              </a>
            ))}
            <a
              data-testid="booking-cta-mobile"
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex w-fit items-center rounded-full bg-[#14213D] text-[#F9F8F6] px-6 py-3 text-sm"
            >
              {t.nav.book}
            </a>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
